import * as React from 'react';
import { cn } from '../../lib/utils';
import { useReducedMotion } from '../../Pages/Landing/motion/useReducedMotion';

const DEFAULT_VINYL_SIZE = 88;

export function Vinyl({
  image,
  alt = '',
  size = DEFAULT_VINYL_SIZE,
  spinning = false,
  speed = 8,
  className,
}) {
  const reduced = useReducedMotion();
  const shouldSpin = spinning && !reduced;
  const fillParent = size === 'fill';
  const px = typeof size === 'number' ? size : DEFAULT_VINYL_SIZE;

  return (
    <div
      className={cn('relative shrink-0 rounded-full', fillParent && 'h-full w-full', className)}
      style={fillParent ? undefined : { width: px, height: px }}
      aria-hidden={!alt}
    >
      <div
        className={cn(
          'relative h-full w-full rounded-full shadow-[0_6px_14px_-8px_rgb(0_0_0/0.45)]',
          shouldSpin && 'animate-sp-spin'
        )}
        style={
          shouldSpin
            ? { animationDuration: `${speed}s` }
            : undefined
        }
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, transparent 12%, rgb(var(--sp-ink-rgb) / 0.15) 13%, transparent 14%),
              conic-gradient(from 0deg, #1a1a22, #2d2d38, #14141a, #333340, #1a1a22),
              radial-gradient(circle at 30% 25%, rgb(255 255 255 / 0.35), transparent 45%)
            `,
          }}
        />
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full border border-white/10 bg-sp-ink shadow-inner"
          style={
            fillParent
              ? { width: '38%', height: '38%' }
              : { width: px * 0.38, height: px * 0.38 }
          }
        >
          {image ? (
            <img src={image} alt={alt} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-sp-ink-2" />
          )}
        </div>
        <div
          className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sp-canvas shadow-sm ring-1 ring-black/20"
          style={
            fillParent
              ? { width: '6%', height: '6%', minWidth: 6, minHeight: 6 }
              : { width: Math.max(6, px * 0.06), height: Math.max(6, px * 0.06) }
          }
        />
      </div>
    </div>
  );
}

export function Equalizer({
  bars = 4,
  playing = true,
  className,
  barClassName,
}) {
  const reduced = useReducedMotion();
  const count = Math.min(5, Math.max(3, bars));

  return (
    <span
      className={cn('inline-flex items-end gap-[2px]', className)}
      aria-hidden
    >
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className={cn(
            'w-[3px] rounded-full bg-current',
            playing && !reduced && 'animate-sp-eq',
            barClassName
          )}
          style={{
            height: 10 + (i % 3) * 2,
            animationDelay: playing && !reduced ? `${i * 0.12}s` : undefined,
          }}
        />
      ))}
    </span>
  );
}

// sm/lg are fixed-size thumbnails; md fills its grid cell.
const SLEEVE_WIDTH = { sm: 'w-24 shrink-0', md: 'w-full', lg: 'w-56 shrink-0' };

export function Sleeve({
  image,
  alt = '',
  href,
  children,
  size = 'md',
  className,
}) {
  const reduced = useReducedMotion();
  const [tilt, setTilt] = React.useState({ x: 0, y: 0 });
  const rootRef = React.useRef(null);

  const handleMove = event => {
    if (reduced || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: py * -6, y: px * 6 });
  };

  const resetTilt = () => setTilt({ x: 0, y: 0 });

  const inner = (
    <div
      ref={rootRef}
      className={cn(
        'group relative cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-sp-blue focus-visible:ring-offset-2',
        SLEEVE_WIDTH[size] || SLEEVE_WIDTH.md,
        className
      )}
      style={{ perspective: 800 }}
      onMouseMove={handleMove}
      onMouseLeave={resetTilt}
      onBlur={resetTilt}
      tabIndex={href ? 0 : undefined}
    >
      <div
        className="relative w-full transition-transform duration-sp ease-sp"
        style={{
          transform: reduced
            ? undefined
            : `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transformStyle: 'preserve-3d',
        }}
      >
        <div className="relative aspect-square w-full overflow-hidden">
          <div
            className={cn(
              'pointer-events-none absolute top-0 z-0 aspect-square w-[88%] transition-transform duration-sp ease-sp',
              'left-[12%] translate-x-0',
              'group-hover:translate-x-[33%] group-hover:rotate-6',
              'group-focus-within:translate-x-[33%] group-focus-within:rotate-6'
            )}
          >
            <Vinyl image={image} alt="" size="fill" className="h-full w-full" />
          </div>
          <div
            className={cn(
              'relative z-10 aspect-square w-[88%] overflow-hidden rounded-lg shadow-sm ring-1 ring-black/10',
              'transition-transform duration-sp ease-sp group-hover:-translate-x-0.5 group-focus-within:-translate-x-0.5'
            )}
          >
            {image ? (
              <img src={image} alt={alt} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full bg-sp-line" />
            )}
          </div>
        </div>
      </div>
      {children ? (
        <div className="mt-3 text-left text-sm font-medium text-sp-ink">{children}</div>
      ) : null}
    </div>
  );

  if (href) {
    return (
      <a href={href} className={cn('block no-underline text-inherit', size === 'md' && 'w-full')}>
        {inner}
      </a>
    );
  }

  return inner;
}
